import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSend, FiCheckCircle, FiRefreshCw, FiShield, FiChevronRight } from 'react-icons/fi';
import { v4 as uuid } from 'uuid';
import { supportChatApi as api } from '../../services/supportChatApi';
import useChatPolling from './useChatPolling';
import './styles.css';

const base = '/api/v1/support-chat';
const merge = (old, added) => [...new Map([...old, ...added].map(row => [row._id, row])).values()]
  .sort((a, b) => a._id.localeCompare(b._id));

export default function ChatThread({ conversationId, support = false, profile, onCreated, onChanged }) {
  const { t, i18n } = useTranslation('supportChat');
  const failure = () => t('thread.failureGeneric');
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(Boolean(conversationId));
  const [sending, setSending] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [olderLoading, setOlderLoading] = useState(false);
  const initialized = useRef(false);
  const readThrough = useRef(null);
  const pending = useRef(null);
  const list = useRef(null);
  const follow = useRef(true);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = async signal => {
    try {
      const { data } = await api.get(`${base}/conversations/${conversationId}/messages`, { signal });
      if (signal?.aborted || !alive.current) return;
      setMessages(old => merge(old, data.messages));
      setConversation(data.conversation);
      if (!initialized.current) setHasMore(data.hasMore);
      initialized.current = true;
      const messageIds = data.messages
        .filter(message => !message.readAt && message.direction === (support ? 'restaurant' : 'support'))
        .map(message => message._id);
      const signature = messageIds.join(',');
      if (messageIds.length && follow.current && readThrough.current !== signature && document.hasFocus() && !document.hidden) {
        await api.patch(`${base}/conversations/${conversationId}/read`, { messageIds }, { signal });
        if (signal?.aborted || !alive.current) return;
        readThrough.current = signature;
        onChanged?.();
      }
      setError('');
    } catch (err) {
      if (!signal?.aborted && alive.current) setError(failure(err));
    } finally {
      if (!signal?.aborted && alive.current) setLoading(false);
    }
  };

  useChatPolling(load, 5000, Boolean(conversationId));

  useEffect(() => {
    if (follow.current && list.current) {
      list.current.scrollTop = list.current.scrollHeight;
    }
  }, [messages]);

  const send = async event => {
    if (event) event.preventDefault();
    if (!text.trim() || sending) return;
    const body = text.trim();
    if (!pending.current || pending.current.text !== body) pending.current = { text: body, clientId: uuid() };
    setSending(true);
    setError('');
    try {
      const url = conversationId ? `${base}/conversations/${conversationId}/messages` : `${base}/mine/messages`;
      const { data } = await api.post(url, pending.current);
      if (!alive.current) return;
      pending.current = null;
      setText('');
      follow.current = true;
      setMessages(old => merge(old, [data.message]));
      setConversation(old => (old ? { ...old, status: 'open' } : old));
      if (!conversationId) onCreated?.(data.conversationId);
      onChanged?.();
    } catch (err) {
      if (alive.current) setError(failure(err));
    } finally {
      if (alive.current) setSending(false);
    }
  };

  const older = async () => {
    if (!messages.length || olderLoading) return;
    setOlderLoading(true);
    try {
      const height = list.current?.scrollHeight || 0;
      const { data } = await api.get(`${base}/conversations/${conversationId}/messages`, {
        params: { before: messages[0]._id },
      });
      if (!alive.current) return;
      follow.current = false;
      setMessages(old => merge(old, data.messages));
      setHasMore(data.hasMore);
      const messageIds = data.messages
        .filter(message => !message.readAt && message.direction === (support ? 'restaurant' : 'support'))
        .map(message => message._id);
      if (messageIds.length && document.hasFocus() && !document.hidden) {
        await api.patch(`${base}/conversations/${conversationId}/read`, { messageIds });
        if (!alive.current) return;
        onChanged?.();
      }
      requestAnimationFrame(() => {
        if (list.current) list.current.scrollTop += list.current.scrollHeight - height;
      });
    } catch (err) {
      if (alive.current) setError(failure(err));
    } finally {
      if (alive.current) setOlderLoading(false);
    }
  };

  const changeStatus = async () => {
    setChangingStatus(true);
    try {
      const status = conversation.status === 'open' ? 'closed' : 'open';
      await api.patch(`${base}/conversations/${conversationId}/status`, { status });
      if (!alive.current) return;
      setConversation(old => ({ ...old, status }));
      onChanged?.();
    } catch (err) {
      if (alive.current) setError(failure(err));
    } finally {
      if (alive.current) setChangingStatus(false);
    }
  };

  const identity = conversation || profile;

  return (
    <div className="eo-chat-thread">
      {identity && (
        <div className="eo-chat-identity">
          <span className="eo-chat-avatar">
            {(identity.restaurant || identity.name)?.charAt(0)?.toUpperCase() || 'P'}
          </span>
          <div>
            <strong>{identity.restaurant || identity.name}</strong>
            <span>
              {identity.name && identity.restaurant ? `${identity.name} · ` : ''}
              {identity.email}
            </span>
          </div>
          {support && conversation && (
            <button
              type="button"
              className="eo-chat-status"
              onClick={changeStatus}
              disabled={changingStatus}
            >
              {conversation.status === 'closed' ? <FiRefreshCw /> : <FiCheckCircle />}
              {conversation.status === 'closed' ? t('thread.reopen') : t('thread.end')}
            </button>
          )}
        </div>
      )}

      <div
        className="eo-chat-messages"
        ref={list}
        role="log"
        aria-label={t('thread.messagesAria')}
        aria-live="polite"
        onScroll={() => {
          const el = list.current;
          if (el) follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        <div className="eo-chat-trust-note">
          <FiShield />
          <span>{t('thread.trustNote')}</span>
        </div>

        {hasMore && (
          <button
            type="button"
            className="eo-chat-older"
            disabled={olderLoading}
            onClick={older}
          >
            {olderLoading ? t('thread.loadingOlder') : t('thread.loadOlder')}
          </button>
        )}

        {loading && (
          <div className="eo-chat-loading-state" role="status">
            <span className="eo-chat-spinner" />
            <span>{t('thread.loadingConversation')}</span>
          </div>
        )}

        {!loading && !messages.length && (
          <div className="eo-chat-welcome">
            <span>👋</span>
            <h3>{t('thread.welcomeHeading')}</h3>
            <p>{t('thread.welcomePrompt')}</p>
            <div className="eo-chat-quick-chips">
              {[
                { emoji: '📦', text: t('thread.quickChips.orders') },
                { emoji: '⚙️', text: t('thread.quickChips.menu') },
                { emoji: '🤖', text: t('thread.quickChips.bot') },
                { emoji: '💳', text: t('thread.quickChips.billing') },
                { emoji: '👨‍💻', text: t('thread.quickChips.specialist') },
              ].map(chip => (
                <button
                  key={chip.text}
                  type="button"
                  className="eo-chat-quick-chip"
                  onClick={() => {
                    setText(`${chip.emoji} ${chip.text}`);
                    const composer = document.getElementById('eo-support-message');
                    if (composer) composer.focus();
                  }}
                >
                  <span className="eo-chat-chip-emoji">{chip.emoji}</span>
                  <span className="eo-chat-chip-text">{chip.text}</span>
                  <FiChevronRight />
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map(message => {
          const mine = message.direction === (support ? 'support' : 'restaurant');
          return (
            <div className={`eo-chat-message ${mine ? 'is-mine' : ''}`} key={message._id}>
              <small>
                {message.senderName}
                {message.direction === 'support' ? t('thread.officialSuffix') : ''}
              </small>
              <p>{message.text}</p>
              <time
                dateTime={message.createdAt}
                title={new Date(message.createdAt).toLocaleString(i18n.language)}
              >
                {new Date(message.createdAt).toLocaleString(i18n.language, {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {mine && (
                  <span className="eo-chat-receipt">
                    <span className="eo-chat-receipt-icon">{message.readAt ? '✓✓' : '✓'}</span>
                    {message.readAt ? t('thread.readLabel') : t('thread.sentLabel')}
                  </span>
                )}
              </time>
            </div>
          );
        })}
      </div>

      {conversation?.status === 'closed' && (
        <p className="eo-chat-notice">{t('thread.closedNotice')}</p>
      )}

      {error && <div className="eo-chat-error" role="alert">{error}</div>}

      <form className="eo-chat-composer" onSubmit={send}>
        <label className="eo-chat-sr" htmlFor="eo-support-message">
          {t('thread.messageLabel')}
        </label>
        <div className="eo-chat-composer-box">
          <textarea
            id="eo-support-message"
            placeholder={t('thread.placeholder')}
            value={text}
            maxLength={4000}
            disabled={sending}
            onChange={event => setText(event.target.value)}
            rows={1}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                send(event);
              }
            }}
          />
          <button
            type="submit"
            disabled={!text.trim() || sending}
            aria-label={t('thread.sendAria')}
          >
            {sending ? '…' : <FiSend />}
          </button>
        </div>
        <div className="eo-chat-composer-hint">
          <span>{t('thread.hint')}</span>
          <span>{text.length}/4000</span>
        </div>
      </form>
    </div>
  );
}
